import React, { useState, useEffect, useRef } from 'react';
import { X, Target, Zap, Clock, AlertTriangle, Play, Square, Activity, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';

const AK47MagDump = ({ onClose, onComplete }) => {
  const { session } = useAuth();
  const [duration, setDuration] = useState(1);
  const [rate, setRate] = useState(10);
  const [functionName, setFunctionName] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);
  
  const pollIntervalRef = useRef(null);

  // Available functions for testing
  const availableFunctions = [
    'COMPOSITE_0',
    'BatchNorm2d_relu_COMPOSITE_53',
    'MaxPool2d_COMPOSITE_99',
    'Sequential_Bottleneck_BatchNorm2d_relu_COMPOSITE_51',
    'Sequential_Bottleneck_BatchNorm2d_add_COMPOSITE_101',
    'AdaptiveAvgPool2d_0_COMPOSITE_54',
    'Linear_COMPOSITE_133'
  ];

  const handleStartStressTest = async () => {
    try {
      setError('');
      setLogs([]);
      
      const response = await ApiService.post('/api/ak47/fire', {
        duration_minutes: duration,
        rate_per_second: rate,
        function_name: functionName || null
      }, session?.access_token);

      setSessionId(response.session_id);
      setIsRunning(true);
      
      addLog(` AK47 mag dump initiated: ${response.total_payloads} payloads over ${duration} minutes`);
      addLog(` Rate: ${rate} payloads/second`);
      addLog(` Function: ${functionName || 'RANDOM'}`);
      
      // Start polling for updates
      startPolling(response.session_id);
      
    } catch (err) {
      setError(`Failed to start mag dump: ${err.message}`);
    }
  };

  const handleStopStressTest = async () => {
    if (!sessionId) return;
    
    try {
      await ApiService.post(`/api/ak47/cease-fire/${sessionId}`, {}, session?.access_token);
      addLog('🛑 Cease fire command sent');
      setIsRunning(false);
      stopPolling();
    } catch (err) {
      setError(`Failed to stop mag dump: ${err.message}`);
    }
  };

  const startPolling = (sessionId) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        // Get session status
        const statusResponse = await ApiService.get(`/api/ak47/status/${sessionId}`, session?.access_token);
        
        if (!statusResponse.active) {
          setIsRunning(false);
          stopPolling();
          addLog(`✅ Mag dump completed: ${statusResponse.payloads_fired} payloads fired`);
          onComplete({ 
            payloads_fired: statusResponse.payloads_fired,
            session_id: sessionId 
          });
        }
        
        // Get overall stats
        const statsResponse = await ApiService.get('/api/ak47/stats', session?.access_token);
        setStats({
          ...statusResponse,
          ...statsResponse
        });
        
      } catch (err) {
        console.error('Polling error:', err);
        // Don't set error state for polling failures to avoid UI flicker
      }
    }, 2000); // Poll every 2 seconds
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      if (isRunning && sessionId) {
        // Send cease-fire when modal closes
        ApiService.post(`/api/ak47/cease-fire/${sessionId}`, {}, session?.access_token).catch(console.error);
      }
    };
  }, [isRunning, sessionId]);

  const totalPayloads = duration * 60 * rate;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Target className="h-6 w-6" />
            <h2 className="text-xl font-bold">AK47 Mag Dump</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {!isRunning ? (
            /* Configuration Form */
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <span className="text-yellow-800 font-medium">Mag Dump Warning</span>
                </div>
                <p className="text-yellow-700 mt-2 text-sm">
                  This will rapidly fire payloads to the AK47 Redis queue for performance testing. 
                  Monitor system resources during the test.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 10 minutes</p>
                </div>

                {/* Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Zap className="h-4 w-4 inline mr-1" />
                    Rate (payloads/second)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={rate}
                    onChange={(e) => setRate(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 50/second</p>
                </div>
              </div>

              {/* Function Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Target className="h-4 w-4 inline mr-1" />
                  Target Function (optional)
                </label>
                <select
                  value={functionName}
                  onChange={(e) => setFunctionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Random Functions</option>
                  {availableFunctions.map(func => (
                    <option key={func} value={func}>{func}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Leave blank to test random functions</p>
              </div>

              {/* Stats Preview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Test Configuration</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Payloads:</span>
                    <span className="font-bold ml-2">{totalPayloads.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Est. Duration:</span>
                    <span className="font-bold ml-2">{duration} minute{duration !== 1 ? 's' : ''}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Rate:</span>
                    <span className="font-bold ml-2">{rate}/sec</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Function:</span>
                    <span className="font-bold ml-2">{functionName || 'Random'}</span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {/* Start Button */}
              <button
                onClick={handleStartStressTest}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
              >
                <Play className="h-5 w-5" />
                <span> FIRE AK47</span>
              </button>
            </div>
          ) : (
            /* Running Status */
            <div className="space-y-6">
              {/* Status Header */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Activity className="h-5 w-5 text-red-600 animate-pulse" />
                    <span className="font-bold text-red-800">AK47 FIRING</span>
                  </div>
                  <button
                    onClick={handleStopStressTest}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center space-x-2 transition-colors"
                  >
                    <Square className="h-4 w-4" />
                    <span>Cease Fire</span>
                  </button>
                </div>
              </div>

              {/* Live Stats */}
              {stats && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <BarChart3 className="h-5 w-5 text-gray-600" />
                    <h3 className="font-medium text-gray-900">Live Statistics</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Payloads Fired:</span>
                      <span className="font-bold ml-2 text-red-600">{stats.payloads_fired?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Queue Depth:</span>
                      <span className="font-bold ml-2">{stats.queue_depth?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Rate:</span>
                      <span className="font-bold ml-2">{stats.rate_per_second}/sec</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Jobs:</span>
                      <span className="font-bold ml-2">{stats.total_jobs_submitted?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Console Log */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Console Output</h3>
                <div className="bg-black text-green-400 font-mono text-xs p-4 rounded-lg h-64 overflow-y-auto">
                  {logs.map((log, index) => (
                    <div key={index} className="mb-1">{log}</div>
                  ))}
                  {logs.length === 0 && (
                    <div className="text-gray-500">Waiting for output...</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AK47MagDump;